import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaMicrophone } from 'react-icons/fa';
import { useSpeechToText } from '../Speech/SpeechToText';
import { ContentCopy as CopyIcon } from '@mui/icons-material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { ArrowDownward as ArrowDownIcon } from '@mui/icons-material';
import { v4 as uuid } from 'uuid';
import splitStringByJson from 'src/utils/splitString';
import {
  ThemeProvider,
  createTheme,
  Box,
  Container,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  Avatar,
  IconButton,
  Drawer,
  useMediaQuery,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Tooltip,
  keyframes
} from '@mui/material';
import {
  Send as SendIcon,
  Menu as MenuIcon,
  Chat as ChatIcon,
  Close as CloseIcon,
  AttachFile as AttachFileIcon,
} from '@mui/icons-material';
//import axios from 'axios';
import { getAuthToken } from 'src/utils/auth'
import MiniChatbot from '../MiniChatbot/Minichatbot';
import CollapsibleReferences from '../CollapsibleReferences/References';
// src/utils/axios.ts


/*
const axiosInstance = axios.create({
    baseURL: process.env.REACT_APP_API_URL // Base URL for the local host
   
 },
);
*/

// Define keyframes for typing animation
const typingAnimation = keyframes`
  0% { opacity: 0.4; }
  50% { opacity: 1; }
  100% { opacity: 0.4; }
`;

// Define keyframes for cursor blinking
const blinkAnimation = keyframes`
  0%, 50% { opacity: 1; }
  51%, 100% { opacity: 0; }
`;

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#10a37f',
    },
    background: {
      default: '#ffffff',
      paper: '#f7f7f8',
    },
    text: {
      primary: '#374151',
    },
  },
  typography: {
    fontFamily: '"Söhne", "Helvetica Neue", "Arial", sans-serif',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
  },
});

interface Message {
  role: string;
  content: string;
  attachment: string;
  citations?: Citation[];
}

interface Citation {
  generatedResponsePart: {
    textResponsePart: {
      span: {
        start: number;
        end: number;
      };
      text: string;
    };
  };
  retrievedReferences: RetrievedReference[];
}

interface RetrievedReference {
  content: {
    text: string;
  };
  location: {
    s3Location: {
      uri: string;
    };
  };
}

interface Attachment {
  name: string;
  data: string;
  type: string;
}

// Enhanced Typing Indicator Component
const TypingIndicator = () => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '12px 16px',
        backgroundColor: 'background.default',
        borderRadius: 2,
        maxWidth: '120px',
        mb: 1
      }}
    >
      
      <Box sx={{ display: 'flex', gap: '3px', alignItems: 'center' }}>
        {[0, 1, 2].map((index) => (
          <Box
            key={index}
            sx={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              backgroundColor: 'primary.main',
              animation: `${typingAnimation} 1.4s infinite`,
              animationDelay: `${index * 0.2}s`
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default function LiveChatbot() {
  const [messages, setMessages] = useState<Message[]>([
    { 
      role: 'assistant', 
      content: 'Hello! I am your assistant for OpenChat. How can I assist you today?', 
      attachment: "",
      // Optional: citations: []  // You can add this if needed
    },
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [selectedSource, setSelectedSource] = useState(1);
  const [isTyping, setIsTyping] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [attachment, setAttachment] = useState<Attachment | null>(null);
  const [userInitial, setUserInitial] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [isListening, setIsListening] = useState(false);
  const [globalinterval, setGlobalinterval] = useState<any>();
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [copyableMessages, setCopyableMessages] = useState<{ [key: number]: boolean }>({});
  const [isStopped, setIsStopped] = useState(false);
  const [abortController, setAbortController] = useState<AbortController | null>(null);

  const handleStopResponse = () => {
    // setIsStopped(true);
    setIsTyping(false);
    clearInterval(globalinterval)
    // Abort ongoing streaming request
    if (abortController) {
      abortController.abort();
    }
  };

  const checkScrollPosition = useCallback(() => {
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
      setIsAtBottom(
        scrollHeight - scrollTop - clientHeight < 20 // Small threshold for "at bottom"
      );
    }
  }, []);

  useEffect(() => {
    if (messagesContainerRef.current) {
      if (isAtBottom) {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      }
    }
  }, [messages, isAtBottom, isTyping]);

  // Add scroll event listener
  useEffect(() => {
    const currentContainer = messagesContainerRef.current;
    if (currentContainer) {
      currentContainer.addEventListener('scroll', checkScrollPosition);
      return () => {
        currentContainer.removeEventListener('scroll', checkScrollPosition);
      };
    }
  }, [checkScrollPosition]);

  if (isFirstLoad) {
    const fetchAuthData = async () => {
      const { idToken, sessionId, username } = await getAuthToken();
      setUserInitial(username.charAt(0).toUpperCase());
    };
    fetchAuthData();
    setIsFirstLoad(false);
  }

  const handleChange = (event: SelectChangeEvent<number>) => {
    const value = Number(event.target.value);
    
    // If the selected source is the same, do nothing
    if (value === selectedSource) return;
  
    // Define initial messages with a type guard method
    const getInitialMessage = (source: number): string => {
      switch(source) {
        case 1:
          return 'Hello! I am your assistant for OpenChat. How can I assist you today?';
        case 2:
          return 'Hello! I am your assistant for Selling Guide. How can I assist you today?';
        case 3:
          return 'Hello! I am your assistant for Sharepoint content. How can I assist you today?';
        case 5:
          return 'Hello! I am your assistant for Self Employment Guide. How can I assist you today?';
        case 6:
          return 'Hello! I am your assistant for Written Corresponence Guide. How can I assist you today?';
        case 7:
          return 'Hello! I am your assistant from the resources in Xactus Vault. How can I assist you today?';
        case 8:
          return 'Hello! I am your Virtual Assistant. How can I assist you today?'; 
        case 9:
          return 'Hello! I am your assistant from the resources in Archwell Technology Sharepoint. How can I assist you today?';
        case 10:
          return 'Hello! I am your assistant for FHA. How can I assist you today?';
        case 11:
          return 'Hello! I am Maya. How can I assist you today?';
        case 12:
          return 'Hello! I am your assistant for Processing. How can I assist you today?';
        default:
          return 'Hello! How can I assist you today?';
      }
    };
  
    // Comprehensive reset of all relevant states
    setMessages([
      { 
        role: 'assistant', 
        content: getInitialMessage(value), 
        attachment: "" 
      }
      
    ]);
    // Reset all relevant states
    setInputMessage('');
    setAttachment(null);
    setSessionId(uuid());
    setIsTyping(false);
    setIsStopped(false);
    
    // Clear any ongoing intervals or streaming processes
    if (globalinterval) {
      clearInterval(globalinterval);
      setGlobalinterval(null);
    }
    
    // Abort any ongoing request
    if (abortController) {
      abortController.abort();
      setAbortController(null);
    }
    
    // Reset copyable messages state
    setCopyableMessages({});
  
    // Set the new source
    setSelectedSource(value);
  };

  const readFileAsync = (file: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const content = await readFileAsync(file);
      const base64 = content.split(",")[1]; // Extracting base64 string from data URI 
      
      const fileTypeMapping: { [key: string]: string} = {
        "application/pdf": "pdf",
        "application/msword": "docx",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
        "image/png": "png",
        "image/jpeg": "jpeg",
        "image/jpg": "jpg"
      };
      
      const fileExt = fileTypeMapping[file.type];
  
      setAttachment({
        name: file.name,
        data: base64,
        type: fileExt
      });
    }
  };

  const removeAttachment = () => {
    setAttachment(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

const [sessionId, setSessionId] = useState<string>(uuid());;

const handleSendMessage = async () => {
  if (inputMessage.trim() !== '') {
    // Add user message to chat
    setMessages(prevMessages => [...prevMessages, { role: 'user', content: inputMessage, attachment: attachment?.name || "" }]);
    setInputMessage('');
    setAttachment(null);
    setIsTyping(true);
    setIsStopped(false);

    try {
      // Handle Maya API call
      if (selectedSource === 11) {
        const mayaResponse = await fetch('https://xw37updwkh.execute-api.us-east-1.amazonaws.com/dev/askMaya', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            prompt: inputMessage
          })
        });

        if (!mayaResponse.ok) {
          throw new Error(`HTTP error! status: ${mayaResponse.status}`);
        }

        const mayaData = await mayaResponse.json();
        
        // Convert HTML tags to text formatting
        const parseHTMLToText = (htmlString: string): string => {
          let parsedContent = htmlString;
          
          // Replace line break tags with newlines
          parsedContent = parsedContent.replace(/<br\s*\/?>/gi, '\n');
          parsedContent = parsedContent.replace(/<\/p>/gi, '\n\n');
          parsedContent = parsedContent.replace(/<p[^>]*>/gi, '');
          
          // Replace heading tags
          parsedContent = parsedContent.replace(/<h[1-6][^>]*>/gi, '\n');
          parsedContent = parsedContent.replace(/<\/h[1-6]>/gi, '\n\n');
          
          // Replace div tags
          parsedContent = parsedContent.replace(/<\/div>/gi, '\n');
          parsedContent = parsedContent.replace(/<div[^>]*>/gi, '');
          
          // Replace list items
          parsedContent = parsedContent.replace(/<li[^>]*>/gi, '• ');
          parsedContent = parsedContent.replace(/<\/li>/gi, '\n');
          parsedContent = parsedContent.replace(/<\/?[uo]l[^>]*>/gi, '\n');
          
          // Replace emphasis tags
          parsedContent = parsedContent.replace(/<strong[^>]*>(.*?)<\/strong>/gi, '**$1**');
          parsedContent = parsedContent.replace(/<b[^>]*>(.*?)<\/b>/gi, '**$1**');
          parsedContent = parsedContent.replace(/<em[^>]*>(.*?)<\/em>/gi, '*$1*');
          parsedContent = parsedContent.replace(/<i[^>]*>(.*?)<\/i>/gi, '*$1*');
          
          // Replace remaining HTML tags
          parsedContent = parsedContent.replace(/<[^>]*>/g, '');
          
          // Decode HTML entities
          parsedContent = parsedContent.replace(/&nbsp;/g, ' ');
          parsedContent = parsedContent.replace(/&amp;/g, '&');
          parsedContent = parsedContent.replace(/&lt;/g, '<');
          parsedContent = parsedContent.replace(/&gt;/g, '>');
          parsedContent = parsedContent.replace(/&quot;/g, '"');
          parsedContent = parsedContent.replace(/&#39;/g, "'");
          
          // Clean up extra whitespace and normalize line breaks
          return parsedContent
            .replace(/\n\s*\n\s*\n/g, '\n\n') // Replace multiple newlines with double newline
            .replace(/[ \t]+/g, ' ') // Replace multiple spaces/tabs with single space
            .trim(); // Remove leading/trailing whitespace
        };
        
        const rawContent = mayaData.message || 'No response received from Maya API';
        const parsedContent = parseHTMLToText(rawContent);
        
        // Add the response to messages
        setMessages(prevMessages => [...prevMessages, { 
          role: 'assistant', 
          content: parsedContent, 
          attachment: '' 
        }]);
        
        setIsTyping(false);
        return;
      }

      // Prepare the request payload
      const payload: {
        prompt: string;
        model: number;
        file?: string;
        file_type?: string;
        sessionId?: string;
        
      } = {
        prompt: inputMessage,
        model: selectedSource,
        file: attachment?.data,
        file_type: attachment?.type
        };

      // Add sessionId if it exists
      console.log(sessionId, 'sessionId**********')
      if (sessionId) {
        payload.sessionId = sessionId;
      }
      console.log(payload, 'payload**********')

        const controller = new AbortController();
        setAbortController(controller);
      // For streaming response (model 1 / OpenChat)
        // DON'T add an empty assistant message here - let the typing indicator handle it

        
        const { idToken } = await getAuthToken();

        const response = await fetch('https://www.mai-backend-service.com/agentChatStream', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          },
          body: JSON.stringify({
            prompt: inputMessage,
            model: selectedSource,
            file: attachment?.data,
            file_type: attachment?.type,
            sessionId
          }),
          signal: controller.signal,
        });

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        let fullResponse = '';
        let agent_citation = '';
        let agent_citation_json = {'citation': ''};
        let citation_list = [];
        let isFirstChunk = true; // Flag to track first chunk
        
        while (true) {
          const { done, value } = await reader?.read() || {};
          if (done || isStopped) break;
          const chunk = decoder.decode(value);
          // console.log('decoded chunk', chunk)
          let [beforeJson, jsonPart, afterJson, jsonChunk] = splitStringByJson(chunk)
          // console.log("beforeJson", beforeJson);
          // console.log("jsonPart", jsonPart); 
          // console.log("afterJson", afterJson); 
          // console.log("jsonChunk", jsonChunk);

          try {
            if (jsonChunk) {

              if (jsonChunk === "firstHalfJson"){
                agent_citation += jsonPart
                continue
              }
              if (jsonChunk === "secondHalfJson" || jsonChunk === "completeJson"){
                agent_citation += jsonPart
              }

              // console.log('agent_citation', agent_citation)
              agent_citation_json = JSON.parse(agent_citation)
              citation_list.push(agent_citation_json.citation)
              agent_citation = ''
              // console.log('jsonPart agent_citation_json', agent_citation_json)
              // console.log('jsonPart agent_citation_json list', agent_citation_json.citation)
            } 

          } catch (error) {
            console.log("error parsing in chunk ", error);
          }

          if (afterJson) {
            console.log("afterJson", afterJson)
            fullResponse += afterJson;
          } else if (beforeJson) {
            fullResponse += beforeJson;
          }

          // On first chunk with content, add the assistant message
          if (isFirstChunk && fullResponse.trim()) {
            setMessages(prevMessages => [...prevMessages, { role: 'assistant', content: fullResponse.trim(), attachment: '' }]);
            setIsTyping(false);
            isFirstChunk = false;
          } else if (!isFirstChunk) {
            // Update existing assistant message
            setMessages(prevMessages => {
              const newMessages = [...prevMessages];
              const lastMessageIndex = newMessages.length - 1;
              
              newMessages[lastMessageIndex] = {
                ...newMessages[lastMessageIndex],
                content: fullResponse.trim()
              };
              return newMessages;
            });
          }
        }

        // Final update with citations
        setMessages(prevMessages => {
          const newMessages = [...prevMessages];
          const lastMessageIndex = newMessages.length - 1;
          
          //@ts-ignore
          newMessages[lastMessageIndex] = {
            ...newMessages[lastMessageIndex],
            content: fullResponse.trim(),
            ...(citation_list.length > 0 ? { citations: citation_list } : {})
          };
          return newMessages;
        });
      
       setIsTyping(false);

      // Existing non-streaming logic for other sources     
      }catch (error) {
        if (error instanceof Error) {
          if (error.name === 'AbortError') {
            console.log("Request Aborted");
          } else {
            console.error('Error fetching bot response:', error);
            setMessages(prevMessages => [...prevMessages, { 
              role: 'assistant', 
              content: `An error occurred: ${error}`, 
              attachment: '' 
            }]);
          }
        } 
        else if (!isStopped) {
          console.error('Error fetching bot response:', error);
          setMessages(prevMessages => [
            ...prevMessages,
            { role: 'assistant', content: `An error occurred: ${error}`, attachment: '' },
          ]);
        }
        else {
          console.error('Unexpected error:', error);
        }
        setIsTyping(false);
      }
    }
  };
  const stopButton = isTyping && (
    <button 
      onClick={handleStopResponse}
      disabled={!isTyping}
      style={{
        position: 'relative',
        right: '33px', // Adjust based on the send button and mic button
        top: '50%',
        transform: 'translateY(-50%)',
        backgroundColor: '#ff5454',
        color: 'white',
        border: 'none',
        borderRadius: '50%',
        width: '24px',
        height: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '12px',
        cursor: 'pointer',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        zIndex: 10
      }}
    >
      ×
    </button>
  );

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const sidebarContent = (
    <Box sx={{ width: 260, height: '100%', bgcolor: '#202123', color: 'white', p: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <ChatIcon sx={{ fontSize: 32, mr: 1 }} />
        <Typography variant="h6">m AI</Typography>
      </Box>
      <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
        <InputLabel id="source-select-label" sx={{ color: 'white' }}>Source</InputLabel>
        <Select
          labelId="source-select-label"
          value={selectedSource}
          onChange={handleChange}
          disabled={isTyping}
          label="Source"
          sx={{ color: 'white', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.23)' } }}
        >
          <MenuItem value={1}>OpenChat</MenuItem>
          <MenuItem value={5}>Self Employment Guide</MenuItem>
          <MenuItem value={2}>Selling Guide</MenuItem>
          <MenuItem value={6}>Written Correspondence</MenuItem>
          <MenuItem value={7}>Xactus Vault</MenuItem>
          <MenuItem value={8}>Virtual Assistant</MenuItem> 
          <MenuItem value={9} disabled>Technology Sharepoint</MenuItem>
          <MenuItem value={10}>FHA</MenuItem>
          <MenuItem value={11}>Maya</MenuItem>
          <MenuItem value={12}>Processing</MenuItem>
          
        </Select>
      </FormControl>
    </Box>
  );

  const renderMessageContent = (content: string) => {
    // console.log('Original content:', content);
    
    // First, explicitly replace any remaining \l with newline
    const processedContent = content.replace(/\\l/g, '\n');
    
    // console.log('Processed content:', processedContent);
  
    return processedContent.split('\n').map((line, index) => (
      <React.Fragment key={index}>
        {line}
        {index < processedContent.split('\n').length - 1 && <br />}
      </React.Fragment>
    ));
  };
  
  const handleResult = (transcript: React.SetStateAction<string>) => {
    setInputMessage(transcript);
    setIsListening(false);
  };

  const handleError = (error: any) => {
    setIsListening(false);
  };

  const {startListening, stopListening, isSupported} = useSpeechToText(
    handleResult,
    handleError
  );

  const toggleListening = () => {
    if(!isSupported) return;
    if(isListening){
      stopListening();
      setIsListening(false);
    }
    else{
      startListening();
      setIsListening(true);

    }
  }

  const styles ={
    micButton: {
    padding: '10px 15px',
    color: '#fff',
    borderRadius: '15px',
    border: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease',
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth={false} disableGutters sx={{ height: '100vh', display: 'flex' }}>
        {!isMobile && (
          <Box sx={{ width: 295, flexShrink: 0 }}>
            {sidebarContent}
          </Box>
        )}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: 'background.default' }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {isMobile && (
              <IconButton onClick={toggleSidebar} sx={{ mr: 2 }}>
                <MenuIcon />
              </IconButton>
            )}
            <Typography variant="h6">ChatBot</Typography>
            <Avatar sx={{ bgcolor: 'primary.main' }}>{userInitial}</Avatar>
          </Box>
          <Box 
            ref={messagesContainerRef}
            sx={{ 
              flexGrow: 1, 
              overflow: 'auto', 
              p: 2, 
              bgcolor: 'background.paper', 
              position: 'relative' 
            }}
          >
            {/* Scroll Down Button */}
            {!isAtBottom && (
                    <IconButton
                      onClick={() => {
                        if (messagesContainerRef.current) {
                          messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
                          setIsAtBottom(true);
                        }
                      }}
                      sx={{
                        position: 'absolute',
                        bottom: 80,
                        right: 20,
                        zIndex: 10,
                        bgcolor: 'primary.main',
                        color: 'white',
                        '&:hover': {
                          bgcolor: 'primary.dark'
                        },
                        boxShadow: 2,
                        width: 30,
                        height: 30
                      }}
                    >
                      <ArrowDownIcon />
                    </IconButton>
                  )}
  
            <List sx={{ overflow: 'visible' }}>
              {messages.map((message, index) => (
                <ListItem 
                  key={index} 
                  sx={{ 
                    display: 'flex', 
                    justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start', 
                    alignItems: 'flex-start', 
                    mb: 2,
                    position: 'relative'
                  }}
                >
                  {message.role === 'assistant' && (
                    <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>AI</Avatar>
                  )}
                  <Box
                    sx={{
                      maxWidth: '70%',
                      position: 'relative',
                      bgcolor: message.role === 'user' ? 'primary.main' : 'background.default',
                      color: message.role === 'user' ? 'common.white' : 'text.primary',
                      borderRadius: 2,
                      p: 2,
                    }}
                  >                  
                    <Typography variant="body1">
                        {renderMessageContent(message.content)}
                        {message.attachment && (
                          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                            <AttachFileIcon sx={{ mr: 1 }} />
                            <Typography variant="body2" color={message.role === 'user' ? 'inherit' : 'text.secondary'}>
                              {message.attachment}
                            </Typography>
                          </Box>
                        )}
                        {message.citations && message.citations.length > 0 && (
                          <CollapsibleReferences citations={message.citations} />
                        )}
                        {/* Enhanced typing cursor for streaming messages */}
                        {message.role === 'assistant' && isTyping && index === messages.length - 1 && message.content && (
                          <Box 
                            component="span" 
                            sx={{ 
                              display: 'inline-block', 
                              width: 2, 
                              height: 16, 
                              bgcolor: 'primary.main', 
                              ml: 0.5, 
                              animation: `${blinkAnimation} 1s infinite`,
                              verticalAlign: 'middle'
                            }} 
                          />
                        )}
                      </Typography>
                    {message.role === 'assistant' && !isTyping && message.content && 
                      messages.indexOf(message) > 0 && (
                        <Box sx={{ 
                          position: 'absolute', 
                          bottom: -10, 
                          right: -10, 
                          display: 'flex', 
                          alignItems: 'center' 
                        }}>
                          {/* Regenerate Button */}
                          <Tooltip title="Regenerate" arrow>
                            <IconButton 
                              size="small" 
                              onClick={() => {
                                const lastUserMessage = messages[messages.length - 2].content;
                                const lastAttachment = messages[messages.length - 2].attachment;
                                
                                setMessages(prevMessages => prevMessages.slice(0, -2));
                                setInputMessage(lastUserMessage);
                                setAttachment(lastAttachment ? { 
                                  name: lastAttachment, 
                                  data: '', 
                                  type: '' 
                                } : null);
                                
                                handleSendMessage();
                              }} 
                              sx={{ 
                                width: 30, 
                                height: 30, 
                                mr: 1, 
                                color: 'text.secondary', 
                                bgcolor: 'background.paper', 
                                boxShadow: 1,
                                '&:hover': { 
                                  bgcolor: 'background.default' 
                                } 
                              }}
  
                            >
                              <RefreshIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        
                          {/* Copy Button */}
                          <IconButton
                            size="small"
                            onClick={() => {
                              navigator.clipboard.writeText(message.content);
                              setCopyableMessages(prev => ({
                                ...prev,
                                [index]: true
                              }));
                              setTimeout(() => {
                                setCopyableMessages(prev => {
                                  const newState = {...prev};
                                  delete newState[index];
                                  return newState;
                                });
                              }, 2000);
                            }}
                            sx={{ 
                              width: 30,
                              height: 30, 
                              color: 'text.secondary', 
                              bgcolor: 'background.paper', 
                              boxShadow: 1,
                              '&:hover': { 
                                bgcolor: 'background.default' 
                              }
                            }}
  
                          >
                            {copyableMessages[index] ? (
                              <Typography variant="caption" color="primary">Copied!</Typography>
                            ) : (
                              <CopyIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Box>
                      )}
                  </Box>
                  {message.role === 'user' && (
                    <Avatar sx={{ bgcolor: 'grey.500', ml: 2 }}>{userInitial}</Avatar>
                  )}
                </ListItem>
              ))}
              
              {/* Show typing indicator when AI is thinking/typing */}
              {isTyping && (
                <ListItem 
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'flex-start', 
                    alignItems: 'flex-start', 
                    mb: 2,
                    position: 'relative'
                  }}
                >
                  <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>AI</Avatar>
                  <TypingIndicator />
                </ListItem>
              )}
            </List>
          </Box>
          <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'background.default' }}>
            <form 
              onSubmit={(e) => { e.preventDefault(); handleSendMessage(); }}
              onKeyDown={(e)=> {
                if(e.key==='Enter' && !e.shiftKey){
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
            >
              {attachment && (
                <Box sx={{ mb: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="textSecondary">
                    {attachment.name}
                  </Typography>
                  <IconButton size="small" onClick={removeAttachment}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
                
              <Box sx={{ display: 'flex', alignItems: 'center' ,position: 'relative'}}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Type your message here..."
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  sx={{
                    mr: 1,
                    position: 'relative', // Add this
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'background.paper',
                      borderRadius: '0.75rem',
                    },
                    
                  }}
                />
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
                {stopButton}
                <Tooltip title="Mic" arrow>
                  <button
                    type="button"
                    onClick={toggleListening}
                    style={{
                      ...styles.micButton,
                      backgroundColor: isListening ? '#ff4d4d' : '#007bff',
                    }}
                  >
                    <FaMicrophone />
                  </button>
                </Tooltip>
                {selectedSource === 1 && (
                  <IconButton onClick={() => fileInputRef.current?.click()} title='Attachment'>
                    <AttachFileIcon />
                  </IconButton>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  endIcon={<SendIcon />}
                  onClick={handleSendMessage}
                  disabled={isTyping || inputMessage.trim() === ''}
                  sx={{ borderRadius: '0.75rem', minWidth: '40px', height: '40px' }}
                >
                  {isMobile ? '' : 'Send'}
                </Button>
              </Box>
            </form>
          </Box>
        </Box>
      </Container>
      <Drawer
        anchor="left"
        open={isSidebarOpen}
        onClose={toggleSidebar}
      >
        {sidebarContent}
      </Drawer>
      <MiniChatbot/>
    </ThemeProvider>
  );
}